// ============================================
// ANÁLOGOS DE GLP-1 — RDC ANVISA nº 973/2025 + IN ANVISA nº 360/2025, Art. 2º (B350)
// ============================================
// Medicamentos que exigem receita COM RETENÇÃO, no MESMO modelo do antimicrobiano
// (2 vias, uma retida pela farmácia) — mudando a VALIDADE: **90 dias**, não 10.
// Em vigor desde 23/06/2025. A farmácia ainda registra a movimentação no SNGPC.
//
// ✅ FONTE OFICIAL, CONFERIDA (AI_151): o **Art. 2º da IN 360/2025** define a lista;
//    a **RDC 973/2025** define o regime (2 vias, retenção, 90 dias, rotulagem).
//    São apenas 5 substâncias, "isoladas ou em associação" — o "em associação" é o
//    que traz o Soliqua (insulina glargina + lixisenatida) para dentro da regra.
//
// 📌 Sobre a LIXISENATIDA: o Lyxumia (isolado) foi **descontinuado definitivamente
//    no Brasil pela Sanofi em 2020**, então ela NÃO entra no catálogo de prescrição
//    — mas continua aqui, porque a associação (Soliqua) é comercializada e a norma
//    alcança as associações. Decisão de Leonardo na AI_151.
//
// ⚠️ Mesma mecânica do `antimicrobianos.ts` (formato "nome|radical" + início de
//    palavra). Os GENÉRICOS e SIMILARES usam a DCB no nome, então já são pegos pela
//    substância; os nomes COMERCIAIS abaixo existem porque o receituário aceita
//    texto livre e é assim que costumam ser digitados.

// As 5 substâncias do Art. 2º da IN 360/2025.
const SUBSTANCIAS = [
  "semaglutida",
  "liraglutida",
  "dulaglutida",
  "tirzepatida",
  "lixisenatida",
];

// Nomes comerciais correntes no Brasil → substância que representam. Sem eles,
// digitar "Ozempic" produziria receita SIMPLES — exatamente a classe de falha que
// o B349 corrigiu nos antimicrobianos (isoniazida saindo sem retenção).
const MARCAS = [
  "semaglutida|ozempic",
  "semaglutida|wegovy",
  "semaglutida|rybelsus",
  "liraglutida|victoza",
  "liraglutida|saxenda",
  "dulaglutida|trulicity",
  "tirzepatida|mounjaro",
  "lixisenatida|soliqua", // associação com insulina glargina
];

const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const escaparRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

type AnalogoGLP1 = { nome: string; re: RegExp };

const ANALOGOS_GLP1: AnalogoGLP1[] = [...SUBSTANCIAS, ...MARCAS].map((linha) => {
  const [nome, radical] = linha.split("|");
  return {
    nome,
    re: new RegExp(`(?:^|[^a-z0-9])${escaparRegex(normalizar(radical ?? nome))}`),
  };
});

/** Quantas substâncias vieram do Art. 2º da IN 360 (trava de regressão). */
export const TOTAL_GLP1 = SUBSTANCIAS.length;

/**
 * Verifica se o nome do medicamento é um análogo de GLP-1 da RDC 973/2025.
 * Retorna a SUBSTÂNCIA (não a marca digitada) ou null.
 */
export function detectarAnalogoGLP1(nomeMedicamento: string): string | null {
  const nomeNorm = normalizar(nomeMedicamento);
  for (const item of ANALOGOS_GLP1) {
    if (item.re.test(nomeNorm)) {
      return item.nome;
    }
  }
  return null;
}

/**
 * Verifica uma lista de medicamentos e retorna as substâncias encontradas,
 * sem repetição.
 */
export function detectarAnalogosGLP1(
  medicamentos: { nome: string }[]
): string[] {
  const encontrados: string[] = [];
  for (const med of medicamentos) {
    const ativo = detectarAnalogoGLP1(med.nome);
    if (ativo && !encontrados.includes(ativo)) {
      encontrados.push(ativo);
    }
  }
  return encontrados;
}
